import React, { useCallback, useEffect, useRef, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  ColDef,
  DateFilterModule,
  NumberFilterModule,
  PaginationModule,
  QuickFilterModule,
  TextFilterModule,
  ValidationModule,
  ExternalFilterModule,
  ModuleRegistry,
  themeAlpine,
  CustomFilterModule
} from 'ag-grid-community';
import { ClientSideRowModelModule } from 'ag-grid-community'; 
import { User } from '../../services/Types';
import { Alert, Badge, Typography } from '@mui/material';
import { useAllUsers } from '../../hooks/useAllUsers';
import UserIcon from '../../images/User/PersonBadge.svg';

ModuleRegistry.registerModules([
  PaginationModule,
  QuickFilterModule,
  ClientSideRowModelModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  ExternalFilterModule,
  CustomFilterModule,
  ValidationModule /* Development Only */,
]);

interface UserGridProps {
  searchText: string;
  setAccountSelected: (accountSelected: User) => void;
}

export const UserGrid: React.FC<UserGridProps> = ({ searchText, setAccountSelected }) => {
  const gridRef = useRef<AgGridReact>(null);
  const containerStyle = useMemo(() => ({ width: "100%", height: "100%" }), []);
  const gridStyle = useMemo(() => ({ height: "calc(100vh - 225px)", width: "100%" }), []);

  const { users, errorUseAllUsers } = useAllUsers();

  // set the quick filter text to the external search text. Re-run when users load so the filter
  // is applied on initial page load with ?q= in URL (grid api may not be ready until data arrives).
  const prevUsersLengthRef = useRef(0);
  useEffect(() => {
    const applyQuickFilter = () => {
      const api = gridRef.current?.api;
      if (!api) return;
      api.setGridOption("quickFilterText", searchText);
    };
    const hadData = prevUsersLengthRef.current > 0;
    const hasData = users.length > 0;
    prevUsersLengthRef.current = users.length;
    if (hasData && !hadData) {
      const t = setTimeout(applyQuickFilter, 100);
      return () => clearTimeout(t);
    }
    applyQuickFilter();
  }, [searchText, users.length]);

  // Set up the column definitions for the grid
  const defaultColDef = useMemo(() => ({
    filter: true,
    flex: 1,
    hide: true,
    resizable: false,
    minWidth: 50
  }), []);

  const columnDefs: ColDef<User>[] = [
    { field: 'id' as keyof User,
      headerName: 'ID',
      hide: true,
      lockPosition: true,
      cellDataType: "number",
    },
    { headerName: 'Name',
      minWidth: 190,
      hide: false,
      sort: 'asc',
      valueGetter: (params: any) => {
        // Note: keep the space in the pending flag
        const pendingFlag = (params.data.approvalStatus === 'PENDING') ? ' (Pending)' : '';
        return `${pendingFlag} ${params.data?.lastName}, ${params.data?.firstName}`;
      },
      cellRenderer: (params: any) => {
        const needsAttention = params.data.approvalStatus === 'PENDING';
        // return a badge with the title and acronym
        return <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginTop: '10px', paddingBottom: '10px' }}>
          <Badge color="primary" variant={needsAttention ? 'dot' : 'standard'}>
            <img src={UserIcon} alt="User Icon" style={{ width: '36px', height: '36px' }} />
          </Badge>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
            <Typography variant="body2">
              {params.data.lastName}, {params.data.firstName}
            </Typography>
            <Typography
              variant="caption"
              style={{
                lineHeight: '1em',
                textTransform: 'capitalize'
              }}
            >
              {params.data.role.toLowerCase()}
            </Typography>
          </div>
        </div>;
      },
      getQuickFilterText: (params: any) => `
        ${params.data.firstName}
        ${params.data.lastName}
        role:${params.data.role}
        po:${params.data.programOffice?.title}
      `
    },
  ];

  return (
    <div className="ag-theme-alpine" style={gridStyle}>
      {errorUseAllUsers && <Alert variant="filled" severity="error">{errorUseAllUsers}</Alert>}

      <AgGridReact
        ref={gridRef}
        theme={themeAlpine}
        rowData={users}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        onRowClicked={(user) => setAccountSelected(user.data)}
        rowHeight={52}
      />
    </div>
  );
}; 